import {Field, MGLClass} from "mobx-graphlink";
import {name_general} from "../../../Utils/General/SharedPatterns.js";
import {Rect, Vec2, Vec3} from "../../../Utils/ThreeJS/VectorStructs.js";
import {ObjPath} from "./@MapObject/ObjPath.js";
import {ObjMesh} from "./@MapObject/ObjMesh.js";
import {ObjModifier} from "./@MapObject/ObjModifier.js";
import {PathTerrainFeature} from "../../../Terrain/Heights/IsolineTypes.js";

export enum LabelPlacement {
	center = "center",
	left = "left",
	right = "right",
	top = "top",
	bottom = "bottom",
}

export enum ShapeType {
	ellipse = "ellipse",
	rectangle = "rectangle",
}

@MGLClass({table: "mapObjects"})
export class MapObject {
	constructor(data: Partial<MapObject>) { Object.assign(this, data); }

	@Field({$ref: "UUID"})
	id: string;

	@Field({$ref: "UUID"})
	creator: string;

	@Field({type: "number"})
	createdAt: number;

	@Field({$ref: "UUID"})
	map: string;

	// general
	// ==========

	@Field({$ref: "UUID"}, {opt: true})
	parent: string|n;

	@Field({type: "string"}, {opt: true})
	orderKey: string|n;

	@Field({type: "string", pattern: name_general})
	name: string;

	@Field({type: "string"})
	tags: string[]|n;

	@Field({$ref: "Vec2"})
	position: Vec3;

	@Field({type: "number"})
	rotation: number|n;

	@Field({type: "boolean"})
	hideSelf?: boolean|n;

	@Field({type: "boolean"})
	hideSubtree?: boolean|n;

	// visuals
	// ==========

	@Field({type: "boolean"})
	label_show = true;

	@Field({type: "string"})
	label_placement: LabelPlacement|n = LabelPlacement.center;

	@Field({$ref: "Vec2"})
	label_offset: Vec2|n;

	@Field({type: "string"})
	label_textOverride: string|n;

	@Field({type: "number"})
	label_baseWidth_world: number|n;

	@Field({type: "number"})
	label_baseFontHeight_world: number|n;

	@Field({type: "number"})
	label_minWidth_screen: number|n;

	@Field({type: "number"})
	label_maxWidth_screen: number|n;

	@Field({type: "number"})
	label_minHeight_screen: number|n;

	@Field({type: "number"})
	label_maxHeight_screen: number|n;

	@Field({type: "string"})
	shape_type: ShapeType|n;

	@Field({$ref: "Vec2"})
	shape_size: Vec2|n;

	@Field({items: {type: "number"}})
	shape_outlineColor: number[]|n;

	@Field({type: "number"})
	shape_rounding: number|n;

	@Field({type: "number"})
	shape_image_opacity: number|n;

	@Field({type: "string"})
	shape_image_embedded: string|n;

	@Field({type: "boolean"})
	shape_osm_enabled: boolean|n;

	/** What area of earth to display, as a Rect where .x and .width are based on [longitude/west-to-east in degrees], and .y and .height are based on [latitude/south-to-north in degrees]. */
	@Field({$ref: "Rect"})
	shape_osm_region: Rect|n;

	@Field({type: "number"})
	shape_osm_sizeAdjust: number|n;

	@Field({type: "boolean"})
	shape_osm_restrictToShapeBounds: boolean|n;

	@Field({type: "number"})
	shape_osm_zoom_base: number|n;

	@Field({type: "number"})
	shape_osm_zoom_focusL1: number|n;

	// meshes
	// ==========

	//@Field({items: {$ref: "ObjMesh"}})
	@Field({items: {type: "object"}})
	meshes: ObjMesh[] = [];

	// paths
	// ==========

	//@Field({items: {$ref: "ObjPath"}})
	@Field({items: {type: "object"}})
	paths: ObjPath[] = [];

	// modifiers
	// ==========

	//@Field({items: {$ref: "ObjModifier"}})
	@Field({items: {type: "object"}})
	modifiers: ObjModifier[] = [];

	// journal
	// ==========

	@Field({items: {type: "string"}})
	journalSegmentPaths: string[] = []; // format: `${journalEntryID}/${journalSegmentID}` or `${journalEntryID}/${journalSegmentID}/${sceneIndex}`
}